import { Routes as RouterRoutes, Route, Navigate, Outlet } from 'react-router-dom';
import { PatientProtectedRoute } from 'components/PatientProtectedRoute';
import { PatientFormsAccessRoute } from 'components/PatientFormsAccessRoute';
import { Layout } from 'components/Layout';
import Dashboard from 'pages/Dashboard';
import { ProtectedResourcePage } from 'components/ProtectedResourcePage';
import { PORTAL_RESOURCE_TYPES } from 'utils/constants';
import AppointmentShow from 'pages/Appointments/Show';
import ConversationShow from 'pages/Conversations/Show';
import Appointments from 'pages/Appointments';
import Conversations from 'pages/Conversations';
import Billing from 'pages/Billing';
import ChargesShow from 'pages/Billing/Charges/Show';
import TreatmentPlan from 'pages/TreatmentPlan'
import TreatmentPlanShow from 'pages/TreatmentPlan/Show'
import TreatmentPlanReview from 'pages/TreatmentPlan/Review'
import MembershipPage from 'pages/Membership';
import Profile from 'pages/Profile';
import PersonalInfo from 'pages/Profile/PersonalInfo';
import Insurance from 'pages/Profile/Insurance';
import MedicalHistory from 'pages/Profile/MedicalHistory';
import Consent from 'pages/Profile/Consent';
import Hipaa from 'pages/Profile/Hipaa';
import TreatmentConsents from 'pages/Profile/TreatmentConsents';
import ProfileDashboard from 'pages/Profile/ProfileDashboard';
import Settings from 'pages/Settings';
import { Toaster } from 'react-hot-toast';
import Auth from 'pages/Auth';

const ProtectedNavShell = () => (
  <PatientProtectedRoute>
    <Layout>
      <Outlet />
    </Layout>
  </PatientProtectedRoute>
);

// Profile step pages, mounted under both /profile (full portal) and /forms (single form, no chrome).
const PROFILE_STEP_ROUTES = [
  { path: 'personal', element: <PersonalInfo /> },
  { path: 'insurance', element: <Insurance /> },
  { path: 'medical', element: <MedicalHistory /> },
  { path: 'hipaa', element: <Hipaa /> },
  { path: 'consent', element: <Consent /> },
  { path: 'treatment-consents', element: <TreatmentConsents /> },
];

const profileStepRoutes = PROFILE_STEP_ROUTES.map(({ path, element }) => (
  <Route key={path} path={path} element={element} />
));

// Resource show pages — DOB auth gate shown inline if unauthenticated.
// Add a new entry here to support a new resource type.
const RESOURCE_SHOW_PAGES = [
  { type: PORTAL_RESOURCE_TYPES.PATIENTS, element: <Navigate to="/portal/profile" replace /> },
  { type: PORTAL_RESOURCE_TYPES.APPOINTMENTS, element: <AppointmentShow /> },
  { type: PORTAL_RESOURCE_TYPES.TREATMENT_PLANS, element: <TreatmentPlanShow /> },
];

export function Routes() {
  return (
    <>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />

      <RouterRoutes>
        {RESOURCE_SHOW_PAGES.map(({ type, element }: any) => (
          <Route
            key={type}
            path={`${type}/:encodedResourceId`}
            element={<ProtectedResourcePage resourceType={type}>{element}</ProtectedResourcePage>}
          />
        ))}

        {/*
          Conversations show page: DOB gate guards only this URL (not the list).
          Wraps <Conversations /> (the sidebar layout) so ConversationShow renders
          inside its Outlet — giving the same split-panel layout as in-app navigation.
        */}
        <Route
          path={`${PORTAL_RESOURCE_TYPES.CONVERSATIONS}/:encodedResourceId`}
          element={
            <ProtectedResourcePage resourceType={PORTAL_RESOURCE_TYPES.CONVERSATIONS}>
              <Conversations />
            </ProtectedResourcePage>
          }
        >
          <Route index element={<ConversationShow />} />
        </Route>

        {/* Magic-link auth entry: /portal/:uid/:token */}
        <Route path=":uid/:token" element={<Auth />} />

        {/* Mobile app WebView: one profile form with no header/sidebar/stepper, reached via the magic link's `next`. */}
        <Route element={<PatientProtectedRoute><PatientFormsAccessRoute /></PatientProtectedRoute>}>
          <Route path="forms" element={<Profile embedded />}>
            {profileStepRoutes}
          </Route>
        </Route>

        <Route element={<ProtectedNavShell />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="conversations" element={<Conversations />} />
          <Route path="billing" element={<Billing />} />
          <Route path="billing/charge/:id" element={<ChargesShow />} />
          <Route path="treatment_plans" element={<TreatmentPlan />} />
          <Route path="treatment_plans/:id/review" element={<TreatmentPlanReview />} />
          <Route path="membership" element={<MembershipPage />} />
          <Route element={<PatientFormsAccessRoute />}>
            <Route path="profile" element={<Profile />}>
              <Route index element={<ProfileDashboard />} />
              {profileStepRoutes}
            </Route>
          </Route>
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Route>
      </RouterRoutes>
    </>
  );
}
